// PirepDetail — slide-over card for a tapped PIREP.
//
// Same visual footprint as AirspaceDetail (right-anchored, ~96 wide,
// closeable). Two sections:
//   1. RAW \u2014 the original PIREP text monospaced
//   2. PLAIN \u2014 a human-readable narrative built from parsePirepRaw()
//
// Plus a key/value rollup at the top showing the most operationally
// relevant fields (over, flight level, aircraft type, hazardClass +
// observation time).

import { parsePirepRaw, plainTextPirep } from '../lib/pirep';
import DetailCard from '../components/DetailCard';

export interface PirepSeed {
  /** Properties dict from the GeoJSON feature \u2014 we read defensively. */
  properties: Record<string, unknown>;
}

interface Props {
  pirep: PirepSeed;
  onClose: () => void;
}

export default function PirepDetail({ pirep, onClose }: Props) {
  const p = pirep.properties;
  const raw = String(p.rawOb || '');
  const parsed = raw ? parsePirepRaw(raw) : null;
  const plain = parsed ? plainTextPirep(parsed) : null;

  const reportType = String(p.airepType || 'PIREP').toUpperCase();
  const isUrgent = !!p.urgent || reportType === 'UUA';

  // Header dot color matches the map's hazard class (so the user
  // can confirm they tapped the right report).
  const hazardColors: Record<string, string> = {
    urgent: 'bg-red-500',
    turb:   'bg-orange-500',
    icing:  'bg-cyan-400',
    wshear: 'bg-yellow-400',
    wx:     'bg-purple-500',
    cloud:  'bg-slate-400',
    other:  'bg-gray-500',
  };
  const hazardClass = String(p.hazardClass || 'other');
  const dot = hazardColors[hazardClass] ?? 'bg-gray-500';

  // Compose the title: aircraft + station, with urgency badge.
  const aircraft = String(p.aircraft || p.acType || '');
  const station = String(p.icaoId || parsed?.station || '');
  const title = aircraft ? `${aircraft}` : station ? `Pilot Report at ${station}` : 'Pilot Report';

  // Rollup rows.
  const rows: Array<[string, string]> = [];
  if (parsed?.over) rows.push(['Over', parsed.over]);
  if (parsed?.timeUtc) rows.push(['Time', `${parsed.timeUtc}Z`]);
  if (parsed?.flightLevel) rows.push(['Altitude', humanFlAlt(parsed.flightLevel)]);
  if (parsed?.aircraftType) rows.push(['Type', parsed.aircraftType]);
  if (typeof p.tempC === 'number') rows.push(['OAT', `${p.tempC}\u00b0C`]);
  if (parsed?.turbulence) rows.push(['Turbulence', parsed.turbulence]);
  if (parsed?.icing) rows.push(['Icing', parsed.icing]);
  if (parsed?.wind) rows.push(['Wind', parsed.wind]);
  if (parsed?.weather) rows.push(['Wx', parsed.weather]);
  if (typeof p.obsTime === 'string' && p.obsTime) {
    rows.push(['Observed', friendlyTime(String(p.obsTime))]);
  }

  // The eyebrow row contains an optional FL badge after the type label;
  // pass it as JSX via the eyebrow prop.
  const eyebrowNode = (
    <>
      <span className="text-[10px] tracking-widest text-cockpit-300">
        {isUrgent ? 'PIREP \u00b7 URGENT' : reportType}
      </span>
      {parsed?.flightLevel && (
        <span className="ml-1 px-1.5 py-0.5 rounded bg-cockpit-700 border border-cockpit-600 font-mono text-[10px] text-cockpit-100">
          {humanFlAlt(parsed.flightLevel)}
        </span>
      )}
    </>
  );

  return (
    <DetailCard
      headerDot={dot}
      eyebrow={eyebrowNode}
      title={title}
      subtitle={station && aircraft ? `at ${station}` : undefined}
      onClose={onClose}
      rollup={rows}
    >
      {plain && (
        <div>
          <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">PLAIN TEXT</div>
          <div className="bg-cockpit-700/20 border border-cockpit-700 rounded-md px-3 py-2 text-[12px] text-cockpit-100 leading-snug">
            {plain}
          </div>
        </div>
      )}
      {raw && (
        <div>
          <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">RAW</div>
          <div className="bg-cockpit-900/60 border border-cockpit-700 rounded-md px-3 py-2 font-mono text-[11px] text-cockpit-100 whitespace-pre-wrap break-words leading-snug">
            {raw}
          </div>
        </div>
      )}
    </DetailCard>
  );
}

function humanFlAlt(fl: string): string {
  const t = fl.trim();
  if (/^DURD$/i.test(t)) return 'descending';
  if (/^DURC$/i.test(t)) return 'climbing';
  if (/^UNKN$/i.test(t)) return 'alt unknown';
  if (/^SFC$/i.test(t)) return 'surface';
  if (/^\d{3}$/.test(t)) return `FL${t}`;
  if (/^\d+$/.test(t)) return `${(parseInt(t, 10) * 100).toLocaleString()} ft`;
  return t;
}

function friendlyTime(iso: string): string {
  return iso.replace('T', ' ').replace(/:\d{2}\.\d{3}Z$/, 'Z').replace(/:\d{2}Z$/, 'Z');
}
